/* global console, document, requestAnimationFrame, HTMLButtonElement, HTMLInputElement, HTMLSelectElement */

import { getDwtDefraSubmissionsDateFieldRendererDefaults } from '../../dwt-defra-submissions/utils/dwt-defra-submissions-date-defaults.util';
import { getDwtPayloadsDateFieldRendererDefaults } from '../../dwt-payloads/utils/dwt-payloads-date-defaults.util';
import { getMaterialSummaryDateFieldRendererDefaults } from '../../material-summary/utils/material-summary-date-field-defaults.util';
import { getInputValue } from '../../shared/dom.util';
import type { TabularSheetWriteMode } from '../../shared/tabular-sheet-write-mode.model';
import { buildTraceFetchFingerprintString } from '../report-fetch/utils/build-trace-fetch-fingerprint.util';
import { validateReportFetchDomInputs } from '../report-fetch/utils/validate-report-fetch-dom-inputs.util';
import { getReportFetchHandler } from '../report-fetch';
import { syncRecordTypeFieldForReportingTarget } from '../record-type';
import { ReportingTargetFilterRegistry } from '../reporting-target/reporting-target-filter-registry';
import { renderReportingTargetPanels } from '../reporting-target/utils/render-reporting-target-panels.util';
import { insertAndActivateTraceWorksheet } from '../utils/insert-and-activate-trace-worksheet.util';
import { buildTraceSheetFilterPersistedFromDom } from '../utils/trace-sheet-filter-snapshot.util';
import {
    clearReportFilterFieldHighlights,
    setReportFilterFieldHighlights,
} from '../utils/report-filter-field-validation-highlights.util';
import type { ReportingTaskPaneFiltersDeps } from './reporting-filters.ports';
import { isTraceRefreshDataAllowed } from './utils/trace-refresh-eligibility.util';

const FETCH_SUMMARY_LINE_IDS = [
    'fetch-summary-line-1',
    'fetch-summary-line-2',
    'fetch-summary-line-3',
] as const;

const REPORT_TARGET_SELECT_ID = 'report-target';

const REFRESH_REPORT_DATA_BUTTON_ID = 'refresh-report-data';

export type ReportFetchIntent = 'replace' | 'refresh';

export class ReportingTaskPaneFilters {
    private readonly registry = new ReportingTargetFilterRegistry();

    /** Fingerprint of the last successful fetch (target + org + normalized API filter); enables Refresh data. */
    private lastSuccessfulFetchFingerprint: string | null = null;

    constructor(private readonly deps: ReportingTaskPaneFiltersDeps) {}

    /** Clears refresh/replace session state (e.g. when the user disconnects). */
    resetTabularWriteSession(): void {
        this.lastSuccessfulFetchFingerprint = null;
        this.syncRefreshButtonState();
    }

    renderPanelsForCurrentTarget(): void {
        const target = getInputValue(REPORT_TARGET_SELECT_ID);
        const definition = this.registry.getFilterByReportingTarget(target);
        syncRecordTypeFieldForReportingTarget(definition);
        renderReportingTargetPanels(definition, this.deps.userInfo.getDynamicFilterOptions(), {
            ...getMaterialSummaryDateFieldRendererDefaults(),
            ...getDwtPayloadsDateFieldRendererDefaults(),
            ...getDwtDefraSubmissionsDateFieldRendererDefaults(),
        });
        clearReportFilterFieldHighlights();
        this.syncRefreshButtonState();
    }

    handleReportingTargetChange(): void {
        this.renderPanelsForCurrentTarget();
        this.resetReportingUi({ clearSummary: true });
    }

    /** Refreshes the access token when due, loads user-derived filter data, then re-renders panels. */
    async refreshUserInfoAndRenderPanels(): Promise<void> {
        if (!this.deps.tokens.hasConnectedSession()) {
            return;
        }
        const tokenData = this.deps.tokens.getTokenData();
        let accessToken = tokenData?.accessToken;
        if (!accessToken) {
            return;
        }

        if (this.deps.tokens.shouldRefreshAccessToken()) {
            const refreshed = await this.deps.tokens.refreshAccessToken();
            if (!refreshed) {
                this.deps.onSyncUi();
                return;
            }
            accessToken = this.deps.tokens.getTokenData()?.accessToken;
            if (!accessToken) {
                this.deps.onSyncUi();
                return;
            }
        }

        await this.deps.userInfo.ensureFacilitiesLoaded(accessToken);
        this.renderPanelsForCurrentTarget();
    }

    /**
     * Clears optional success emphasis on the summary, or the whole summary panel when switching context.
     */
    resetReportingUi(options?: { clearSummary?: boolean }): void {
        const summaryEl = document.getElementById('fetch-summary');
        if (options?.clearSummary) {
            this.clearFetchSummaryPanel();
        } else if (summaryEl) {
            summaryEl.classList.remove('fetch-summary--prominent');
        }
    }

    /**
     * Enables **Refresh data** only when the current filter fingerprint matches the last successful fetch.
     */
    syncRefreshButtonState(): void {
        const refreshBtn = document.getElementById(
            REFRESH_REPORT_DATA_BUTTON_ID
        ) as HTMLButtonElement | null;
        if (!refreshBtn) {
            return;
        }
        if (!this.deps.tokens.hasConnectedSession()) {
            refreshBtn.disabled = true;
            return;
        }
        const dataSection = document.getElementById('data-section');
        if (dataSection?.getAttribute('aria-busy') === 'true') {
            refreshBtn.disabled = true;
            return;
        }
        const target = getInputValue(REPORT_TARGET_SELECT_ID);
        const fingerprint = buildTraceFetchFingerprintString(target);
        refreshBtn.disabled = !isTraceRefreshDataAllowed(
            this.lastSuccessfulFetchFingerprint,
            fingerprint
        );
    }

    /**
     * Fetches the selected report and writes tabular output to the active worksheet.
     *
     * - **Fetch report** uses `intent: 'replace'` (default): full tabular write from A1; may show overwrite confirm.
     * - **Refresh data** uses `intent: 'refresh'`: values-only into the existing Trace table; skips overwrite confirm;
     *   only allowed when the filter fingerprint matches {@link lastSuccessfulFetchFingerprint}.
     *
     * When the user opts for a new worksheet from the confirm dialog, tabular write mode is **replace**.
     */
    async fetchReport(options?: {
        intent?: ReportFetchIntent;
        skipOverwriteConfirm?: boolean;
        /** When set, fetch for this reporting target id instead of the `#report-target` value. */
        reportingTarget?: string;
    }): Promise<void> {
        const intent: ReportFetchIntent = options?.intent ?? 'replace';
        const target =
            options?.reportingTarget !== undefined && options.reportingTarget.trim() !== ''
                ? options.reportingTarget.trim()
                : getInputValue(REPORT_TARGET_SELECT_ID);
        const targetFilters = this.registry.getFilterByReportingTarget(target);

        if (!targetFilters.fetchImplemented) {
            clearReportFilterFieldHighlights();
            this.resetReportingUi();
            this.updateFetchSummary(
                `The “${targetFilters.displayName}” report cannot be fetched from this add-in yet.`,
                'error'
            );
            return;
        }

        const fetchHandler = getReportFetchHandler(target, this.deps.reportFetchHandlers);

        if (!fetchHandler) {
            clearReportFilterFieldHighlights();
            this.resetReportingUi();
            this.updateFetchSummary(
                `The “${targetFilters.displayName}” report cannot be fetched from this add-in yet.`,
                'error'
            );
            return;
        }

        if (!this.deps.tokens.hasConnectedSession()) {
            clearReportFilterFieldHighlights();
            this.resetReportingUi();
            this.updateFetchSummary('Please connect before fetching data.', 'error');
            return;
        }

        const domValidation = validateReportFetchDomInputs(target);
        if (domValidation.ok === false) {
            this.resetReportingUi();
            setReportFilterFieldHighlights(domValidation.invalidElementIds);
            this.updateFetchSummary(domValidation.message, 'error');
            return;
        }

        clearReportFilterFieldHighlights();

        const fingerprint = buildTraceFetchFingerprintString(target);

        if (
            intent === 'refresh' &&
            !isTraceRefreshDataAllowed(this.lastSuccessfulFetchFingerprint, fingerprint)
        ) {
            this.updateFetchSummary(
                'Refresh is only available when filters match your last successful fetch. Use Fetch report to pull with new filters.',
                'error'
            );
            return;
        }

        let skipOverwriteConfirm = options?.skipOverwriteConfirm ?? false;
        if (intent === 'refresh') {
            skipOverwriteConfirm = true;
        }

        let proceed = true;
        let writeToNewWorksheet = false;
        if (!skipOverwriteConfirm) {
            const confirm =
                await this.deps.fetchConfirm.confirmFetchIfActiveSheetMayBeOverwritten();
            proceed = confirm.proceed;
            writeToNewWorksheet = confirm.writeToNewWorksheet;
        }
        if (!proceed) {
            this.updateFetchSummary(
                'Fetch cancelled. No data was requested or written.',
                'default'
            );
            return;
        }

        if (this.deps.tokens.shouldRefreshAccessToken()) {
            this.setLoadingState(true, 'Refreshing session...');
            await this.waitForUiPaint();
            const refreshed = await this.deps.tokens.refreshAccessToken();
            if (!refreshed) {
                this.setLoadingState(false);
                this.resetReportingUi();
                this.updateFetchSummary(
                    'Your session could not be renewed. Connect to Trace again to continue.',
                    'error'
                );
                this.deps.onSyncUi();
                return;
            }
        }

        const accessToken = this.deps.tokens.getTokenData()?.accessToken;
        if (!accessToken) {
            this.resetReportingUi();
            this.updateFetchSummary('Please connect before fetching data.', 'error');
            this.deps.onSyncUi();
            return;
        }

        const displayName = targetFilters.displayName;
        this.setLoadingState(true, `Fetching ${displayName} from API...`);
        this.updateFetchSummary(`Fetching ${displayName}…`, 'default');
        await this.waitForUiPaint();

        if (writeToNewWorksheet) {
            this.setLoadingState(true, 'Adding worksheet...');
            await this.waitForUiPaint();
            await insertAndActivateTraceWorksheet(displayName);
            this.setLoadingState(true, `Fetching ${displayName} from API...`);
        }

        let tabularWriteMode: TabularSheetWriteMode = this.resolveTabularWriteMode(
            intent,
            fingerprint
        );
        if (writeToNewWorksheet) {
            tabularWriteMode = 'replace';
        }

        try {
            const result = await fetchHandler({
                accessToken,
                targetId: target,
                displayName,
                tabularWriteMode,
                deps: {
                    agreementsApi: this.deps.agreementsApi,
                    materialSummaryApi: this.deps.materialSummaryApi,
                    dwtPayloadsApi: this.deps.dwtPayloadsApi,
                    dwtDefraSubmissionsApi: this.deps.dwtDefraSubmissionsApi,
                    tabularSheetWriter: this.deps.tabularSheetWriter,
                },
            });

            if (result.kind === 'failure') {
                this.resetReportingUi();
                if (result.source === 'validation') {
                    setReportFilterFieldHighlights(result.invalidElementIds ?? []);
                    this.updateFetchSummary(result.message, 'error');
                } else {
                    const logLabel =
                        result.source === 'worksheet'
                            ? 'Excel write failed'
                            : 'Report fetch failed';
                    console.error(logLabel, result.message);
                    this.updateFetchSummary(`Fetch failed: ${result.message}`, 'error');
                }
                return;
            }

            clearReportFilterFieldHighlights();
            this.commitAfterSuccessfulTabularWrite(fingerprint);
            if (result.sheetId !== undefined && this.deps.sheetFilters?.saveAfterSuccessfulFetch) {
                const snapshot = buildTraceSheetFilterPersistedFromDom({
                    targetId: target,
                    definition: targetFilters,
                    fetchFingerprint: fingerprint,
                });
                try {
                    await this.deps.sheetFilters.saveAfterSuccessfulFetch({
                        sheetId: result.sheetId,
                        snapshot,
                    });
                } catch (persistErr) {
                    console.warn(
                        '[trace-reports-excel-addin] Could not persist sheet filters',
                        persistErr
                    );
                }
            }
            this.updateFetchSummarySuccess({
                recordCount: result.recordCount,
                totalCount: result.totalCount,
                sheetName: result.sheetName,
                tableCreated: result.tableCreated,
            });
        } finally {
            this.setLoadingState(false);
        }
    }

    private showFetchSummaryPanel(): void {
        document.getElementById('fetch-summary')?.classList.remove('hidden');
    }

    private clearFetchSummaryPanel(): void {
        const el = document.getElementById('fetch-summary');
        if (!el) {
            return;
        }
        el.classList.remove(
            'hint--success',
            'hint--error',
            'fetch-summary--prominent',
            'fetch-summary--prominent-error'
        );
        this.setFetchSummaryLine(0, '', false);
        this.setFetchSummaryLine(1, '', false);
        this.setFetchSummaryLine(2, '', false);
        el.classList.add('hidden');
    }

    private setFetchSummaryLine(lineIndex: 0 | 1 | 2, text: string, visible: boolean): void {
        const id = FETCH_SUMMARY_LINE_IDS[lineIndex];
        const line = document.getElementById(id);
        if (!line) {
            return;
        }
        line.textContent = text;
        line.classList.toggle('hidden', !visible);
    }

    private updateFetchSummary(message: string, variant: 'default' | 'success' | 'error'): void {
        const el = document.getElementById('fetch-summary');
        if (!el) {
            return;
        }
        this.showFetchSummaryPanel();
        el.classList.remove(
            'hint--success',
            'hint--error',
            'fetch-summary--prominent',
            'fetch-summary--prominent-error'
        );
        if (variant === 'success') {
            el.classList.add('hint--success');
        } else if (variant === 'error') {
            el.classList.add('hint--error', 'fetch-summary--prominent-error');
        }
        this.setFetchSummaryLine(0, message, true);
        this.setFetchSummaryLine(1, '', false);
        this.setFetchSummaryLine(2, '', false);
    }

    private updateFetchSummarySuccess(params: {
        recordCount: number;
        totalCount?: number;
        sheetName: string;
        tableCreated: boolean;
    }): void {
        const el = document.getElementById('fetch-summary');
        if (!el) {
            return;
        }
        this.showFetchSummaryPanel();
        el.classList.remove(
            'hint--success',
            'hint--error',
            'fetch-summary--prominent',
            'fetch-summary--prominent-error'
        );
        el.classList.add('hint--success');

        const fetchedWord = params.recordCount === 1 ? 'record' : 'records';
        this.setFetchSummaryLine(0, `Fetched ${params.recordCount} ${fetchedWord}`, true);

        const totalPart = typeof params.totalCount === 'number' ? String(params.totalCount) : '—';
        this.setFetchSummaryLine(1, `Total available records: ${totalPart}`, true);

        let line3 = `Data written to sheet "${params.sheetName}" starting at cell A1`;
        if (!params.tableCreated) {
            line3 += ' (cell range only; structured table was not created).';
        }
        this.setFetchSummaryLine(2, line3, true);
    }

    private setLoadingState(isLoading: boolean, message?: string): void {
        const loadingState = document.getElementById('loading-state');
        const loadingText = document.getElementById('loading-text');
        const dataSection = document.getElementById('data-section');

        if (loadingState) {
            loadingState.classList.toggle('hidden', !isLoading);
        }

        if (loadingText && message) {
            loadingText.textContent = message;
        }

        if (dataSection) {
            dataSection.setAttribute('aria-busy', isLoading ? 'true' : 'false');
            const controls = dataSection.querySelectorAll<
                HTMLInputElement | HTMLSelectElement | HTMLButtonElement
            >('input, select, button');
            controls.forEach((control) => {
                control.disabled = isLoading;
            });
        }

        if (!isLoading) {
            this.deps.onSyncUi();
        }
    }

    private async waitForUiPaint(): Promise<void> {
        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });
    }

    private resolveTabularWriteMode(
        intent: ReportFetchIntent,
        fingerprint: string | null
    ): TabularSheetWriteMode {
        if (intent === 'replace') {
            return 'replace';
        }
        if (fingerprint === null) {
            return 'replace';
        }
        return 'refresh';
    }

    private commitAfterSuccessfulTabularWrite(fingerprint: string | null): void {
        if (fingerprint !== null) {
            this.lastSuccessfulFetchFingerprint = fingerprint;
        }
    }

    /**
     * Restores refresh eligibility after rehydrating filters from sheet metadata (e.g. worksheet activation).
     */
    applyPersistedSheetFetchFingerprint(fingerprint: string | null): void {
        this.lastSuccessfulFetchFingerprint = fingerprint;
        this.syncRefreshButtonState();
    }
}
